// src/components/ManualUpdate.js
import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Grid,
  useTheme,
  TextField,
  InputAdornment,
  IconButton,
  Button,
  Chip,
  Popover,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";
import styles from "./manualUpdates.module.css";
import { useUser } from "../../functions/userContext";
import { Tabs } from "../../components/shared/tabs/tabs";
import {
  AssignmentLate as JiraIcon,
  Storage as PlatformIcon,
  AttachMoney as VendorIcon,
  Security as VulnerabilityIcon,
  Business as LOBIcon,
  UploadFile as UploadFileIcon,
  Download as DownloadIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Add as AddIcon,
  Delete,
  Save,
  Clear as ClearIcon,
} from "@mui/icons-material";
import GenericJsonEditorTable from "../../components/shared/genericDataEditor/genericDataEditor";

// Custom component that wraps GenericJsonEditorTable and forces it to always be expanded
const ModifiedDataTable = React.forwardRef((props, ref) => {
  return (
    <Box className={styles.dataTableWrapper}>
      <GenericJsonEditorTable
        {...props}
        ref={ref}
        forceExpanded={true} // This will override the internal expanded state
        searchTerm={props.searchTerm}
        activeFilters={props.activeFilters}
        onSchemaUpdate={props.onSchemaUpdate}
        uploadedData={props.uploadedData}
      />
    </Box>
  );
});

// Data section configuration for dynamic rendering
const dataSections = [
  {
    id: "jira",
    title: "Jira Boards",
    description: "Manage Jira board configurations and integrations",
    icon: <JiraIcon />,
    fetchUrl: "static/generic?table=JiraBoard",
    submitUrl: "static/generic?table=JiraBoard",
    deleteUrl: "static/generic/JiraBoard",
    primaryKey: "Id",
    color: "#2684FF", // Jira blue
  },
  {
    id: "vendor",
    title: "Vendor Spend",
    description: "Manage vendor spending data and allocations",
    icon: <VendorIcon />,
    fetchUrl: "static/generic?table=StaticMonthly",
    submitUrl: "static/generic?table=StaticMonthly",
    deleteUrl: "static/generic/StaticMonthly",
    primaryKey: "Id",
    keyValue: "vendor",
    color: "#FF9800", // Orange
  },
  {
    id: "discounts",
    title: "AWS Discounts",
    description: "Set AWS Discount percentages for cost calculations",
    icon: <LOBIcon />,
    fetchUrl: "cloudcosts/awsDiscounts-editor?startYear=2025&startMonth=1&endYear=2025&endMonth=12",
    submitUrl: "cloudcosts/awsDiscounts-batch",
    deleteUrl: "cloudcosts/awsDiscounts-batch",
    primaryKey: "Id",
    keyValue: "discount",
    color: "#673AB7", // Purple
  },
];

const ManualUpdate = () => {
  const theme = useTheme();
  const isLight = theme.palette.mode === "light";
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [tabStates, setTabStates] = useState(
    dataSections.reduce((acc, section) => {
      acc[section.id] = {
        searchTerm: "",
        showFilter: false,
        filterColumn: "",
        filterValue: "",
        filterAnchorEl: null,
        availableColumns: [],
        activeFilters: {},
        uploadedData: null,
        uploadLoading: false,
      };
      return acc;
    }, {})
  );
  const fileInputRef = useRef(null);
  const tableRefs = useRef({});
  const { setShowTabs, setCurrentTab } = useUser();

  // Get current section and its state
  const currentSection = dataSections[activeTabIndex];
  const currentState = tabStates[currentSection.id];

  // Helper to update current tab's state
  const updateTabState = (updates) => {
    setTabStates((prev) => ({
      ...prev,
      [currentSection.id]: {
        ...prev[currentSection.id],
        ...updates,
      },
    }));
  };

  const handleFilterClick = (event) => {
    updateTabState({
      filterAnchorEl: event.currentTarget,
      showFilter: !currentState.showFilter,
    });
  };

  const clearFilter = () => {
    updateTabState({
      filterColumn: "",
      filterValue: "",
      activeFilters: {},
      showFilter: false,
      filterAnchorEl: null,
    });
  };

  const applyFilter = () => {
    if (currentState.filterColumn && currentState.filterValue) {
      updateTabState({
        activeFilters: {
          column: currentState.filterColumn,
          value: currentState.filterValue,
        },
        showFilter: false,
        filterAnchorEl: null,
      });
    }
  };

  const handleCloseFilter = () => {
    updateTabState({
      showFilter: false,
      filterAnchorEl: null,
    });
  };

  // Handle schema updates from the data table to get available columns
  const handleSchemaUpdate = (schema) => {
    if (schema && schema.length > 0) {
      const columns = schema
        .filter((col) => col.display && !["Id", "LastUpdated"].includes(col.name))
        .map((col) => ({
          name: col.name,
          type: col.type,
          label: col.name.replace(/([A-Z])/g, " $1").trim(),
        }));
      updateTabState({ availableColumns: columns });
    }
  };

  // Handle file upload - same logic as in the table
  const handleFileUpload = async (file) => {
    if (!file) return;

    updateTabState({ uploadLoading: true });

    try {
      const data = await file.arrayBuffer();
      const wb = XLSX.read(data);
      const ws = wb.Sheets[wb.SheetNames[0]];
      const json = XLSX.utils.sheet_to_json(ws);

      // Force "Key" to uppercase and present even if keyValue is falsy
      const updated = json.map((r) => ({
        ...r,
        Key: currentSection.keyValue ?? "", // fallback to empty string if keyValue is undefined
      }));

      // Set the uploaded data which will be passed to the table component
      updateTabState({ uploadedData: updated });

      // Clear the uploaded data after the table processes it (longer delay for visual feedback)
      setTimeout(() => {
        updateTabState({ uploadedData: null, uploadLoading: false });
        // Show success toast
        toast.success(
          `Successfully processed ${updated.length} rows from ${file.name}. Adding to table...`
        );
      }, 2000);

      // Clear file input to allow re-uploading the same file
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      // Refresh the current tab data
    } catch (error) {
      console.error("Error processing file:", error);
      toast.error("Error processing file. Please check the file format.");
      updateTabState({ uploadLoading: false });
    }
  };

  // Handle export - same logic as in the table
  const handleExport = (sectionId) => {
    if (tableRefs.current[sectionId] && tableRefs.current[sectionId].exportToExcel) {
      tableRefs.current[sectionId].exportToExcel();
    } else {
      toast.error("Export function not available. Please try again.");
    }
  };

  // ✅ Load saved state
  useEffect(() => {
    setShowTabs(true);
  }, []);

  // Update current tab in context when activeTabIndex changes
  useEffect(() => {
    if (currentSection) {
      setCurrentTab(currentSection.title);
    }
    return () => {
      setCurrentTab(null); // Clear tab when unmounting
    };
  }, [activeTabIndex, currentSection, setCurrentTab]);

  return (
    <Box
      sx={{
        height: "100vh",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        paddingLeft: "5px",
      }}
    >
      {/* Hidden file input for upload - shared across all tabs */}
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: "none" }}
        accept=".xlsx,.xls"
        onChange={async (e) => {
          const file = e.target.files?.[0];
          if (file) {
            await handleFileUpload(file);
            // Clear the input to allow uploading the same file again
            e.target.value = "";
          }
        }}
      />

      {/* Filter Popover - shared across all tabs */}
      <Popover
        open={currentState.showFilter}
        anchorEl={currentState.filterAnchorEl}
        onClose={handleCloseFilter}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        PaperProps={{
          sx: {
            p: 2,
            minWidth: 300,
            backgroundColor: isLight ? "#fff" : "#333333",
            border: `1px solid ${isLight ? "#e0e0e0" : "#666"}`,
            boxShadow: isLight ? "0 4px 20px rgba(0,0,0,0.1)" : "0 4px 20px rgba(0,0,0,0.3)",
          },
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Typography variant="h6" sx={{ color: isLight ? "#000" : "#fff", mb: 1 }}>
            Filter {currentSection.title || "Data"}
          </Typography>

          {/* Active Filters Display */}
          {Object.keys(currentState.activeFilters).length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ color: isLight ? "#666" : "#aaa", mb: 1 }}>
                Active Filters:
              </Typography>
              <Chip
                label={`${currentState.activeFilters.column}: ${currentState.activeFilters.value}`}
                onDelete={() => updateTabState({ activeFilters: {} })}
                size="small"
                sx={{
                  backgroundColor: "rgba(255, 108, 88, 0.1)",
                  color: "#FF6C58",
                  "& .MuiChip-deleteIcon": {
                    color: "#FF6C58",
                  },
                }}
              />
            </Box>
          )}

          {/* Column Selection */}
          <FormControl fullWidth size="small">
            <InputLabel sx={{ color: isLight ? "#666" : "#aaa" }}>Column</InputLabel>
            <Select
              value={currentState.filterColumn}
              onChange={(e) => updateTabState({ filterColumn: e.target.value })}
              label="Column"
              sx={{
                color: isLight ? "#000" : "#fff",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: isLight ? "#ccc" : "#666",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: isLight ? "#999" : "#aaa",
                },
                "& .MuiSvgIcon-root": {
                  color: isLight ? "#666" : "#aaa",
                },
              }}
            >
              {currentState.availableColumns.map((column) => (
                <MenuItem
                  key={column.name}
                  value={column.name}
                  sx={{ color: isLight ? "#000" : "#fff" }}
                >
                  {column.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Value Input */}
          <TextField
            fullWidth
            size="small"
            label="Filter Value"
            value={currentState.filterValue}
            onChange={(e) => updateTabState({ filterValue: e.target.value })}
            placeholder="Enter value to filter by..."
            sx={{
              "& .MuiInputLabel-root": {
                color: isLight ? "#666" : "#aaa",
              },
              "& .MuiOutlinedInput-root": {
                color: isLight ? "#000" : "#fff",
                "& fieldset": {
                  borderColor: isLight ? "#ccc" : "#666",
                },
                "&:hover fieldset": {
                  borderColor: isLight ? "#999" : "#aaa",
                },
              },
            }}
          />

          {/* Action Buttons */}
          <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
            <Button
              variant="outlined"
              size="small"
              onClick={clearFilter}
              startIcon={<ClearIcon />}
              sx={{
                color: isLight ? "#666" : "#aaa",
                borderColor: isLight ? "#ccc" : "#666",
                "&:hover": {
                  borderColor: isLight ? "#999" : "#aaa",
                  backgroundColor: isLight ? "#f5f5f5" : "#404040",
                },
              }}
            >
              Clear
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={applyFilter}
              disabled={!currentState.filterColumn || !currentState.filterValue}
              sx={{
                backgroundColor: "#FF6C58",
                color: "white",
                "&:hover": {
                  backgroundColor: "#e53e3e",
                },
                "&:disabled": {
                  backgroundColor: isLight ? "#e0e0e0" : "#555",
                  color: isLight ? "#666" : "#888",
                },
              }}
            >
              Apply Filter
            </Button>
          </Box>
        </Box>
      </Popover>

      <Tabs activeTab={activeTabIndex} onTabChange={setActiveTabIndex}>
        {dataSections.map((section, index) => (
          <Box
            key={section.id}
            title={section.title}
            sx={{
              height: "calc(100vh - 60px)",
              backgroundColor: "background.default",
              padding: "16px 32px",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Tab-specific Search and Filter Controls */}
            <Box
              sx={{
                mb: 2,
                p: 2,
                borderRadius: 2,
                backgroundColor: isLight ? "rgba(255, 255, 255, 0.9)" : "rgba(51, 51, 51, 0.9)",
                backdropFilter: "blur(10px)",
                border: `1px solid ${isLight ? "rgba(0,0,0,0.1)" : "rgba(255,255,255,0.15)"}`,
                boxShadow: isLight ? "0 2px 8px rgba(0,0,0,0.08)" : "0 2px 8px rgba(0,0,0,0.3)",
                flexShrink: 0,
              }}
            >
              <Grid container spacing={1.5} alignItems="center">
                <Grid item xs={12} md={5}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder={`Search ${section.title || "data"}...`}
                    value={tabStates[section.id].searchTerm}
                    onChange={(e) =>
                      setTabStates((prev) => ({
                        ...prev,
                        [section.id]: { ...prev[section.id], searchTerm: e.target.value },
                      }))
                    }
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ color: isLight ? "#666" : "#aaa" }} />
                        </InputAdornment>
                      ),
                      endAdornment: tabStates[section.id].searchTerm && (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={() =>
                              setTabStates((prev) => ({
                                ...prev,
                                [section.id]: { ...prev[section.id], searchTerm: "" },
                              }))
                            }
                          >
                            <ClearIcon sx={{ fontSize: 18, color: isLight ? "#666" : "#aaa" }} />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        height: "32px",
                        color: isLight ? "#000" : "#fff",
                        backgroundColor: isLight ? "#fff" : "#333333",
                        "& fieldset": {
                          borderColor: isLight ? "#ccc" : "#666",
                        },
                        "&:hover fieldset": {
                          borderColor: isLight ? "#999" : "#aaa",
                        },
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={6} md={1}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<FilterListIcon />}
                    onClick={handleFilterClick}
                    sx={{
                      height: "32px",
                      color:
                        Object.keys(tabStates[section.id].activeFilters).length > 0
                          ? "#FF6C58"
                          : isLight
                            ? "#666"
                            : "#aaa",
                      borderColor:
                        Object.keys(tabStates[section.id].activeFilters).length > 0
                          ? "#FF6C58"
                          : isLight
                            ? "#ccc"
                            : "#666",
                      backgroundColor:
                        Object.keys(tabStates[section.id].activeFilters).length > 0
                          ? "rgba(255, 108, 88, 0.1)"
                          : "transparent",
                      fontSize: "0.75rem",
                      "&:hover": {
                        borderColor: "#FF6C58",
                        backgroundColor: "rgba(255, 108, 88, 0.1)",
                      },
                    }}
                  >
                    Filter
                  </Button>
                </Grid>
                <Grid item xs={6} md={1}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={
                      tabStates[section.id].uploadLoading ? (
                        <CircularProgress size={16} />
                      ) : (
                        <UploadFileIcon />
                      )
                    }
                    onClick={() => fileInputRef.current?.click()}
                    disabled={tabStates[section.id].uploadLoading}
                    sx={{
                      height: "32px",
                      color: tabStates[section.id].uploadLoading
                        ? "#999"
                        : isLight
                          ? "#666"
                          : "#aaa",
                      borderColor: tabStates[section.id].uploadLoading
                        ? "#999"
                        : isLight
                          ? "#ccc"
                          : "#666",
                      fontSize: "0.75rem",
                      "&:hover": !tabStates[section.id].uploadLoading && {
                        borderColor: isLight ? "#999" : "#aaa",
                        backgroundColor: isLight ? "#f5f5f5" : "#404040",
                      },
                      "&:disabled": {
                        opacity: 0.7,
                      },
                    }}
                  >
                    {tabStates[section.id].uploadLoading ? "Processing..." : "Upload"}
                  </Button>
                </Grid>
                <Grid item xs={6} md={1}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    onClick={() => handleExport(section.id)}
                    sx={{
                      height: "32px",
                      color: isLight ? "#666" : "#aaa",
                      borderColor: isLight ? "#ccc" : "#666",
                      fontSize: "0.75rem",
                      "&:hover": {
                        borderColor: isLight ? "#999" : "#aaa",
                        backgroundColor: isLight ? "#f5f5f5" : "#404040",
                      },
                    }}
                  >
                    Export
                  </Button>
                </Grid>
                <Grid item xs={6} md={1}>
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      if (tableRefs.current[section.id]) {
                        tableRefs.current[section.id].addRow();
                      }
                    }}
                    sx={{
                      height: "32px",
                      backgroundColor: "#4CAF50",
                      color: "white",
                      fontSize: "0.75rem",
                      fontWeight: "600",
                      whiteSpace: "nowrap",
                      boxShadow: "0 2px 8px rgba(76, 175, 80, 0.3)",
                      "&:hover": {
                        backgroundColor: "#45a049",
                        transform: "translateY(-1px)",
                        boxShadow: "0 4px 12px rgba(76, 175, 80, 0.4)",
                      },
                    }}
                  >
                    Add
                  </Button>
                </Grid>
                <Grid item xs={6} md={1.5}>
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<Delete />}
                    onClick={() => {
                      if (tableRefs.current[section.id]) {
                        tableRefs.current[section.id].deleteAllRows();
                      }
                    }}
                    sx={{
                      height: "32px",
                      backgroundColor: "#FF6C58",
                      color: "white",
                      fontSize: "0.75rem",
                      fontWeight: "600",
                      whiteSpace: "nowrap",
                      boxShadow: "0 2px 8px rgba(255, 108, 88, 0.3)",
                      "&:hover": {
                        backgroundColor: "#e53e3e",
                        transform: "translateY(-1px)",
                        boxShadow: "0 4px 12px rgba(255, 108, 88, 0.4)",
                      },
                    }}
                  >
                    Delete All
                  </Button>
                </Grid>
                <Grid item xs={6} md={1.5}>
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<Save />}
                    onClick={() => {
                      if (tableRefs.current[section.id]) {
                        tableRefs.current[section.id].handleSubmit();
                      }
                    }}
                    sx={{
                      height: "32px",
                      backgroundColor: "#2196F3",
                      color: "white",
                      fontSize: "0.75rem",
                      fontWeight: "600",
                      boxShadow: "0 2px 8px rgba(33, 150, 243, 0.3)",
                      "&:hover": {
                        backgroundColor: "#1976D2",
                        transform: "translateY(-1px)",
                        boxShadow: "0 4px 12px rgba(33, 150, 243, 0.4)",
                      },
                    }}
                  >
                    Save
                  </Button>
                </Grid>
              </Grid>
            </Box>

            {/* Upload Loading Indicator */}
            {tabStates[section.id].uploadLoading && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 2,
                  p: 2,
                  borderRadius: 2,
                  backgroundColor: isLight ? "rgba(255, 255, 255, 0.9)" : "rgba(51, 51, 51, 0.9)",
                  backdropFilter: "blur(10px)",
                  border: `1px solid ${isLight ? "rgba(0,0,0,0.1)" : "rgba(255,255,255,0.15)"}`,
                  boxShadow: isLight ? "0 2px 8px rgba(0,0,0,0.08)" : "0 2px 8px rgba(0,0,0,0.3)",
                  mb: 2,
                  flexShrink: 0,
                }}
              >
                <CircularProgress size={20} sx={{ color: "#FF6C58" }} />
                <Typography
                  variant="body2"
                  sx={{
                    color: isLight ? "#000" : "#fff",
                    fontWeight: 600,
                  }}
                >
                  Processing upload and adding rows to table...
                </Typography>
              </Box>
            )}

            {/* Content Area */}
            <Box
              sx={{
                flex: 1,
                overflow: "auto",
                "&::-webkit-scrollbar": {
                  width: "6px",
                },
                "&::-webkit-scrollbar-track": {
                  backgroundColor: "transparent",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: isLight ? "rgba(0,0,0,0.2)" : "rgba(255,255,255,0.2)",
                  borderRadius: "3px",
                  "&:hover": {
                    backgroundColor: isLight ? "rgba(0,0,0,0.4)" : "rgba(255,255,255,0.4)",
                  },
                },
              }}
            >
              <ModifiedDataTable
                ref={(el) => (tableRefs.current[section.id] = el)}
                fetchUrl={section.fetchUrl}
                submitUrl={section.submitUrl}
                deleteUrl={section.deleteUrl}
                primaryKey={section.primaryKey}
                keyValue={section.keyValue}
                maxHeight="100%"
                color={section.color}
                searchTerm={tabStates[section.id].searchTerm}
                activeFilters={tabStates[section.id].activeFilters}
                onSchemaUpdate={handleSchemaUpdate}
                uploadedData={tabStates[section.id].uploadedData}
              />
            </Box>
          </Box>
        ))}
      </Tabs>
    </Box>
  );
};

export default ManualUpdate;
